import { getDataFetchingClientRequestState } from 'marketplace-ui-common/dataFetchingClient/utils/getDataFetchingClientRequestState';
import { useParams } from 'quartz-routing/react-router';
import { useDiscoveryCollections } from '../../collections/hooks/useDiscoveryCollections';
import { useUmbrellaCollections } from '../../collections/hooks/useUmbrellaCollections';
import { useGetCollectionBySlugQuery } from './useGetCollectionBySlugQuery';
/** Fetches collection based on URL slug or most recent collection, with umbrella collection support */
export function useCollectionQuery(options = {
  mode: 'url'
}) {
  const {
    mode
  } = options;
  const {
    collectionSlug
  } = useParams();
  const umbrellaCollections = useUmbrellaCollections();
  const umbrellaCollectionsRequestState = getDataFetchingClientRequestState(umbrellaCollections.error, umbrellaCollections.loading);
  const umbrellaCollection = mode === 'url' && collectionSlug ? umbrellaCollections.getUmbrellaCollectionBySlug(collectionSlug) : undefined;
  const slugQuery = useGetCollectionBySlugQuery({
    skip: mode !== 'url' || !collectionSlug || !!umbrellaCollection
  });
  const dsResult = useDiscoveryCollections({
    skip: mode === 'url'
  });
  if (mode === 'url' && !collectionSlug) {
    return {
      data: undefined,
      error: undefined,
      isUmbrellaCollection: false,
      loading: false
    };
  }
  if (umbrellaCollection && umbrellaCollectionsRequestState !== 'PENDING') {
    return {
      data: umbrellaCollection,
      error: undefined,
      isUmbrellaCollection: true,
      loading: false
    };
  }
  if (mode === 'url') {
    var _slugQuery$data;
    if (slugQuery.loading || umbrellaCollectionsRequestState === 'PENDING') {
      return {
        data: undefined,
        error: undefined,
        isUmbrellaCollection: false,
        loading: true
      };
    }
    const slugResult = (_slugQuery$data = slugQuery.data) === null || _slugQuery$data === void 0 ? void 0 : _slugQuery$data.result;
    if (slugQuery.error || !slugResult) {
      return {
        data: undefined,
        error: new Error('Collection not found'),
        isUmbrellaCollection: false,
        loading: false
      };
    }
    return {
      data: toCollectionWithPreviews(slugResult),
      error: undefined,
      isUmbrellaCollection: false,
      loading: false
    };
  }
  if (dsResult.loading || umbrellaCollectionsRequestState === 'PENDING') {
    return {
      data: undefined,
      error: undefined,
      isUmbrellaCollection: false,
      loading: true
    };
  }
  if (dsResult.error) {
    return {
      data: undefined,
      error: new Error('Failed to load collections'),
      isUmbrellaCollection: false,
      loading: false
    };
  }
  const dsCollection = mode === 'recent' ? dsResult.collections[0] : dsResult.collections.find(c => c.entityId === options.collectionId);
  if (dsCollection) {
    var _dsCollection$title, _dsCollection$descrip, _dsCollection$descrip2, _dsCollection$title2, _dsCollection$descrip3;
    return {
      data: {
        id: dsCollection.entityId,
        slug: dsCollection.slug,
        name: dsCollection.name,
        title: (_dsCollection$title = dsCollection.title) !== null && _dsCollection$title !== void 0 ? _dsCollection$title : undefined,
        description: (_dsCollection$descrip = dsCollection.description) !== null && _dsCollection$descrip !== void 0 ? _dsCollection$descrip : '',
        tagline: (_dsCollection$descrip2 = dsCollection.description) !== null && _dsCollection$descrip2 !== void 0 ? _dsCollection$descrip2 : '',
        itemCount: dsCollection.listingCount,
        displayed: true,
        previewItems: dsCollection.previewItems.map(item => ({
          listingId: item.listingId,
          name: item.name,
          iconUrl: item.iconUrl,
          slug: item.slug
        })),
        updatedAt: undefined,
        htmlPageTitle: (_dsCollection$title2 = dsCollection.title) !== null && _dsCollection$title2 !== void 0 ? _dsCollection$title2 : '',
        metaDescription: (_dsCollection$descrip3 = dsCollection.description) !== null && _dsCollection$descrip3 !== void 0 ? _dsCollection$descrip3 : ''
      },
      error: undefined,
      isUmbrellaCollection: false,
      loading: false
    };
  }
  return {
    data: undefined,
    error: new Error(mode === 'recent' ? 'No collections available' : 'Collection not found'),
    isUmbrellaCollection: false,
    loading: false
  };
}
function toCollectionWithPreviews(result) {
  var _result$title, _result$description, _result$description2, _result$items, _ref, _result$htmlPageTitle, _ref2, _result$metaDescripti;
  return {
    id: result.entityId,
    slug: result.slug,
    name: (_result$title = result.title) !== null && _result$title !== void 0 ? _result$title : result.slug,
    title: result.title,
    description: (_result$description = result.description) !== null && _result$description !== void 0 ? _result$description : '',
    tagline: (_result$description2 = result.description) !== null && _result$description2 !== void 0 ? _result$description2 : '',
    itemCount: result.listingCount,
    previewItems: ((_result$items = result.items) !== null && _result$items !== void 0 ? _result$items : []).map(item => {
      var _item$listingName;
      return {
        listingId: item.listingId,
        name: (_item$listingName = item.listingName) !== null && _item$listingName !== void 0 ? _item$listingName : '',
        iconUrl: item.iconUrl,
        slug: item.slug
      };
    }),
    updatedAt: undefined,
    htmlPageTitle: (_ref = (_result$htmlPageTitle = result.htmlPageTitle) !== null && _result$htmlPageTitle !== void 0 ? _result$htmlPageTitle : result.title) !== null && _ref !== void 0 ? _ref : '',
    metaDescription: (_ref2 = (_result$metaDescripti = result.metaDescription) !== null && _result$metaDescripti !== void 0 ? _result$metaDescripti : result.description) !== null && _ref2 !== void 0 ? _ref2 : ''
  };
}